import React, { Fragment, useState } from 'react';
import XLSX from 'xlsx';
import styled from 'styled-components';
import { find, sum, filter, uniq } from 'ramda';
import toast from '~~elements/Toast';
import ProgressBar from '~~elements/ProgressBar';
import Button from '~~elements/Button';
import { parseExcel, getBoxScore } from '~~services/PbPExcel';
import { getPossibleExistPlayers, sortPossiblePlayers } from '~~services/Player';
import { PlayerResource, SeasonResource } from '~~apis/resource';
import { useTournamentState } from '../useTournament';
import { updateGamePbP, updateGameScoreBoard } from '../service';

const StyledDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0.5rem 0;
  > button,
  input {
    margin-right: 1rem;
  }

  .notification {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    > p {
      margin: 0.2rem 0;
    }
    .success {
      color: green;
    }
    .warning {
      color: gold;
    }
    .error {
      color: red;
    }
  }
`;

const crossLeaguePlayerMapping = [['CPBL', 'CPBLmi', 'AWB']];
const locationNameMapping = {
  台南: '臺南市立棒球場',
  嘉義市: '嘉義市立棒球場',
  澄清湖: '澄清湖棒球場',
  斗六: '斗六棒球場',
  嘉義縣: '嘉義縣棒球場',
  園區: '中國信託公益園區棒球場',
  新莊: '新北市立新莊棒球場',
  天母: '臺北市立天母棒球場',
  花蓮: '花蓮縣立德興棒球場',
  台東: '台東棒球村第一棒球場',
  洲際: '臺中市洲際棒球場',
  樂天桃園: '樂天桃園棒球場',
};

const BatchPbPUpload = ({ teams, games }) => {
  const { tournamentInfo } = useTournamentState();

  const [batchGameTitle, setBatchGameTitle] = useState('讀取並轉換資料');
  const [batchGameCompleted, setBatchGameCompleted] = useState(0);

  const [teamsInPending, setTeamsInPending] = useState([]);
  const [pendingGames, setPendingGames] = useState([]);

  const [uploadingGames, setUploadingGames] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [inputRefresh, setInputRefresh] = useState(0);

  const errorToast = (message) => {
    toast(message, {
      status: 'error',
      second: 5,
    });
  };

  const handleFileOnChange = (e) => {
    e.preventDefault();
    const files = e.target.files;
    if (files.length === 0) {
      setInputRefresh(inputRefresh + 1);
      return;
    }

    const parsedGames = [];
    const includingTeams = [];
    setUploading(true);
    setUploadingGames([]);
    setBatchGameTitle('讀取並轉換資料');
    setBatchGameCompleted(0);
    let parsingCountDown = files.length;
    const parsingProgress = () => {
      parsingCountDown--;
      setBatchGameCompleted(((files.length - parsingCountDown) * 100) / files.length);
      if (parsingCountDown > 0) {
        return;
      }

      setPendingGames(parsedGames);
      setTeamsInPending(includingTeams);
      setInputRefresh(inputRefresh + 1);
      setUploading(false);
    };

    const fileOnLoad = (fileName) => (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const { gameInfo, PbPResult } = parseExcel({
        workbook,
        errorToast,
        allowPlayerCreate: true,
      });

      if (!gameInfo || !PbPResult) {
        parsedGames.push({ fileName, error: true });
        parsingProgress();
        return;
      }

      const awayTeam = find((t) => t.name === gameInfo.away.teamName, teams);
      const homeTeam = find((t) => t.name === gameInfo.home.teamName, teams);
      if (!awayTeam || !homeTeam) {
        errorToast('主場或客場球隊不包含在此賽事中');
        parsedGames.push({ fileName, error: '主場或客場球隊不包含在此賽事中' });
        parsingProgress();
        return;
      }

      const sameSeqGame = find((g) => g.seq === gameInfo.info.gameSeq, games);
      const sameSeqParsingGame = find((g) => g?.gameInfo?.info.gameSeq === gameInfo.info.gameSeq, parsedGames);
      if (sameSeqGame || sameSeqParsingGame) {
        errorToast('比賽編號重複');
        parsedGames.push({ fileName, error: '比賽編號重複' });
        parsingProgress();
        return;
      }

      const location = find(
        (l) => l.name === gameInfo.info.gameLocation || l.name === locationNameMapping[gameInfo.info.gameLocation],
        tournamentInfo.locations,
      );
      if (!location) {
        errorToast('球場不包含在此賽事中');
        parsedGames.push({ fileName, error: '球場不包含在此賽事中' });
        parsingProgress();
        return;
      }

      const completedGameInfo = {
        away: {
          team: awayTeam,
          players: gameInfo.away.players,
          scores: gameInfo.away.scores,
          runs: sum(filter((s) => !Number.isNaN(parseInt(s)), gameInfo.away.scores).map((s) => parseInt(s))),
        },
        home: {
          team: homeTeam,
          players: gameInfo.home.players,
          scores: gameInfo.home.scores,
          runs: sum(filter((s) => !Number.isNaN(parseInt(s)), gameInfo.home.scores).map((s) => parseInt(s))),
        },
        info: {
          ...gameInfo.info,
          location,
        },
      };

      // 檢查用，之後賦予完 uniqid 後重新新增一次
      const BoxScore = getBoxScore({
        gameInfo: completedGameInfo,
        parsedData: PbPResult,
        endOuts: gameInfo.info.endOuts,
        errorToast,
      });
      if (BoxScore === false) {
        parsedGames.push({ fileName, error: true });
        parsingProgress();
        return;
      }

      // 處理要新增跟已經在賽事中的球員
      includingTeams.push(awayTeam.uniqid, homeTeam.uniqid);
      parsedGames.push({
        fileName,
        gameInfo: completedGameInfo,
        PbPResult,
      });
      parsingProgress();
    };

    for (let file of files) {
      const reader = new FileReader();
      reader.onload = fileOnLoad(file.name);
      reader.readAsArrayBuffer(file);
    }
  };

  const getInTournamentPlayers = () => {
    setBatchGameTitle('獲取現有球員');
    setBatchGameCompleted(0);
    return new Promise((resolve, reject) => {
      const inTournamentPlayers = {};
      const uniqTeamsInPending = uniq(teamsInPending);
      if (uniqTeamsInPending.length === 0) {
        resolve({});
      }

      let includingTeamCountDown = uniqTeamsInPending.length;
      const getTeamPlayers = (teamInPendingUniqid, players) => {
        players.forEach((player) => {
          const playerIdentity = `${teamInPendingUniqid};;${player.name};;${player.number}`;
          inTournamentPlayers[playerIdentity] = player;
          // 背號需要調整的時候檢查用
          inTournamentPlayers[`${teamInPendingUniqid};;${player.number}`] = player;
        });
        includingTeamCountDown--;
        setBatchGameCompleted(((uniqTeamsInPending.length - includingTeamCountDown) * 100) / uniqTeamsInPending.length);
        if (includingTeamCountDown > 0) {
          return;
        }
        resolve(inTournamentPlayers);
      };

      uniqTeamsInPending.forEach((teamInPendingUniqid) => {
        PlayerResource.getPlayersInSeasonTeam({
          seasonUniqid: tournamentInfo.uniqid,
          seasonTeamUniqid: teamInPendingUniqid,
        })
          .then(({ data }) => {
            getTeamPlayers(teamInPendingUniqid, data);
          })
          .catch((e) => {
            errorToast('獲取現有球員時發生錯誤，請重新整理頁面');
            reject();
          });
        return;
      });
    });
  };

  const createPlayer = (inTournamentPlayers) => {
    setBatchGameTitle('新建球員');
    setBatchGameCompleted(0);

    return new Promise((resolve, reject) => {
      const need2CreateTeamNumberCheck = {};
      const changeNumberPlayers = {};

      const need2CreateMapping = {};
      const checkOneSidePlayers = ({ team, players }) => {
        players.forEach((player) => {
          const playerIdentity = `${team.uniqid};;${player.name};;${player.number}`;
          if (inTournamentPlayers[playerIdentity] || need2CreateMapping[playerIdentity]) {
            return;
          }

          let newPlayerNumber = player.number;
          let numberChanged = false;
          while (
            need2CreateTeamNumberCheck[`${team.uniqid};;${newPlayerNumber}`] ||
            inTournamentPlayers[`${team.uniqid};;${newPlayerNumber}`]
          ) {
            newPlayerNumber += '*';
            numberChanged = true;
          }
          need2CreateTeamNumberCheck[`${team.uniqid};;${newPlayerNumber}`] = true;
          if (numberChanged) {
            const oldNumber = player.number;
            player.number = newPlayerNumber;
            changeNumberPlayers[playerIdentity] = { team, player, oldNumber, newNumber: newPlayerNumber };
          }

          // 加上 * 號之後可能就有符合的球員了
          if (inTournamentPlayers[`${team.uniqid};;${player.name};;${newPlayerNumber}`]) {
            return;
          }
          need2CreateMapping[playerIdentity] = { team, player };
          return;
        });
      };
      pendingGames.forEach((pendingGame) => {
        if (pendingGame.error) {
          return;
        }
        checkOneSidePlayers(pendingGame.gameInfo.away);
        checkOneSidePlayers(pendingGame.gameInfo.home);
      });

      const need2CreateList = Object.values(need2CreateMapping);
      const need2CreateAmount = need2CreateList.length;
      if (need2CreateAmount === 0) {
        resolve({ inTournamentPlayers, changeNumberPlayers });
        return;
      }

      let need2CreateCountDown = need2CreateAmount;
      const playerCreated = ({ team, player }) => {
        need2CreateCountDown--;
        setBatchGameCompleted(((need2CreateAmount - need2CreateCountDown) * 100) / need2CreateAmount);
        const playerIdentity = `${team.uniqid};;${player.name};;${player.number}`;
        inTournamentPlayers[playerIdentity] = player;
        if (need2CreateCountDown > 0) {
          return;
        }
        resolve({ inTournamentPlayers, changeNumberPlayers });
      };
      need2CreateList.forEach((need2CreatePlayer) => {
        const brandNewPlayer = (playerUniqid) => {
          PlayerResource.createPlayerInSeasonTeamByOrganizer({
            leagueUniqid: tournamentInfo.league.uniqid,
            seasonUniqid: tournamentInfo.uniqid,
            seasonTeamUniqid: need2CreatePlayer.team.uniqid,
            data: {
              player_uniqid: playerUniqid,
              number: need2CreatePlayer.player.number,
              name: need2CreatePlayer.player.name,
              avatar_path: '',
              birthday: '',
              first_position: '',
              second_position: '',
              p_hand: '',
              b_hand: '',
            },
          })
            .then(({ data }) => {
              playerCreated({
                team: need2CreatePlayer.team,
                player: data,
              });
              return;
            })
            .catch((e) => {
              console.error(e);
              errorToast('新建球員時發生錯誤，請重新整理頁面');
              reject();
            });
        };

        const crossLeague = find((m) => m.includes(tournamentInfo.league.uniqid), crossLeaguePlayerMapping);
        if (!crossLeague) {
          brandNewPlayer('');
          return;
        }

        getPossibleExistPlayers({
          name: need2CreatePlayer.player.name,
          leagueUniqids: crossLeague,
          exact: true,
        })
          .then((possiblePlayers) => {
            const sortedPossiblePlayers = sortPossiblePlayers({
              possiblePlayers,
              teamName: need2CreatePlayer.team.name,
              playerNumber: need2CreatePlayer.player.number,
            });
            console.log(sortedPossiblePlayers);
            const possiblePlayer = sortedPossiblePlayers.length >= 1 ? sortedPossiblePlayers[0] : null;
            brandNewPlayer(possiblePlayer ? possiblePlayer.uniqid : '');
            return;
          })
          .catch((e) => {
            console.error(e);
            errorToast('新建球員時發生錯誤，請重新整理頁面');
            reject();
          });
      });
    });
  };

  const BoxScoreAssignPlayerUniqid = ({ inTournamentPlayers, changeNumberPlayers }) => {
    const assignOneSidePlayers = (seasonTeamUniqid, opponentSeasonTeamUniqid, { fielders, pbp }) => {
      const addPlayerUniqid = (isOpponent, { name, number }) => {
        let newNumber = number;
        let playerIdentity = `${isOpponent ? opponentSeasonTeamUniqid : seasonTeamUniqid};;${name};;${number}`;
        if (changeNumberPlayers[playerIdentity]) {
          newNumber = changeNumberPlayers[playerIdentity].newNumber;
          playerIdentity = `${isOpponent ? opponentSeasonTeamUniqid : seasonTeamUniqid};;${name};;${newNumber}`;
        }

        const mappingPlayer = inTournamentPlayers[playerIdentity];
        if (!mappingPlayer) {
          console.log(inTournamentPlayers, playerIdentity);
          errorToast('新建球員時發生錯誤，請重新整理頁面');
          return false;
        }

        return {
          name,
          number: newNumber,
          uniqid: mappingPlayer.uniqid,
        };
      };

      for (let fielder of fielders) {
        fielder.player = addPlayerUniqid(false, fielder.player);
        if (!fielder.player) {
          return false;
        }
      }
      for (let PA of pbp) {
        PA.batter = addPlayerUniqid(false, PA.batter);
        if (!PA.batter) {
          return false;
        }
        PA.pitcher = addPlayerUniqid(true, PA.pitcher);
        if (!PA.pitcher) {
          return false;
        }
      }
      return true;
    };

    for (let pendingGame of pendingGames) {
      if (pendingGame.error) {
        continue;
      }
      if (
        !assignOneSidePlayers(
          pendingGame.gameInfo.away.team.uniqid,
          pendingGame.gameInfo.home.team.uniqid,
          pendingGame.PbPResult.away,
        )
      ) {
        return false;
      }
      if (
        !assignOneSidePlayers(
          pendingGame.gameInfo.home.team.uniqid,
          pendingGame.gameInfo.away.team.uniqid,
          pendingGame.PbPResult.home,
        )
      ) {
        return false;
      }

      pendingGame.BoxScore = getBoxScore({
        gameInfo: pendingGame.gameInfo,
        parsedData: pendingGame.PbPResult,
        endOuts: pendingGame.gameInfo.info.endOuts,
        errorToast,
      });
    }
    return true;
  };

  const createGames = () => {
    const pendingGameAmount = pendingGames.length;
    let pendingCountDown = pendingGameAmount;
    setBatchGameTitle('新建比賽、逐打席紀錄');
    setBatchGameCompleted(0);
    const apiDone = () => {
      pendingCountDown--;
      setBatchGameCompleted(((pendingGameAmount - pendingCountDown) * 100) / pendingGameAmount);
      if (pendingCountDown > 0) {
        return;
      }

      toast('批次新增完成，請重新整理後確認資料', { status: 'success', second: 5 });
    };

    pendingGames.forEach((pendingGame) => {
      if (pendingGame.error) {
        setUploadingGames((pre) => [...pre, { status: 'warning', message: `${pendingGame.fileName} 跳過` }]);
        apiDone();
        return;
      }

      SeasonResource.createGame({
        seasonUniqid: tournamentInfo.uniqid,
        data: {
          seq: pendingGame.gameInfo.info.gameSeq,
          group: '循環賽',
          away: pendingGame.gameInfo.away.team.name,
          home: pendingGame.gameInfo.home.team.name,
          scheduled_start_at: pendingGame.gameInfo.info.gameStartedAt,
          location_uniqid: pendingGame.gameInfo.info.location.uniqid,
          status: 'FINISHED',
          finished: {
            status: 'NORMAL',
            started_at: pendingGame.gameInfo.info.gameStartedAt,
            ended_at: pendingGame.gameInfo.info.gameEndedAt,
            away_runs: pendingGame.gameInfo.away.runs,
            home_runs: pendingGame.gameInfo.home.runs,
          },
        },
      })
        .then(({ data: completedGame }) => {
          const gameUniqid = completedGame.uniqid;
          updateGameScoreBoard(tournamentInfo.uniqid, gameUniqid, {
            seq: pendingGame.gameInfo.info.gameSeq,
            group: '循環賽',
            away: {
              team: pendingGame.gameInfo.away.team.name,
              scores: pendingGame.gameInfo.away.scores,
              runs: pendingGame.gameInfo.away.runs,
            },
            home: {
              team: pendingGame.gameInfo.home.team.name,
              scores: pendingGame.gameInfo.home.scores,
              runs: pendingGame.gameInfo.home.runs,
            },
            info: {
              location_uniqid: pendingGame.gameInfo.info.location.uniqid,
              innings: pendingGame.gameInfo.away.scores.length,
              runs_without_innings: false,
              status: 'FINISHED',
              finished_status: 'NORMAL',
              time: {
                scheduled_start_at: pendingGame.gameInfo.info.gameStartedAt,
                started_at: pendingGame.gameInfo.info.gameStartedAt,
                ended_at: pendingGame.gameInfo.info.gameEndedAt,
              },
            },
          }).then(() => {
            updateGamePbP(tournamentInfo.uniqid, gameUniqid, pendingGame.BoxScore).then(() => {
              setUploadingGames((pre) => [...pre, { status: 'success', message: `${pendingGame.fileName} 完成新增` }]);
              apiDone();
            });
          });
        })
        .catch((e) => {
          console.error(e);
          errorToast(`${pendingGame.fileName} 新增失敗`);
          setUploadingGames((pre) => [...pre, { status: 'error', message: `${pendingGame.fileName} 新增失敗` }]);
        });
    });
  };

  const handleSubmit = () => {
    setUploading(true);
    setBatchGameTitle('新建球員、比賽、逐打席紀錄');
    setBatchGameCompleted(0);

    getInTournamentPlayers()
      .then((inTournamentPlayers) => {
        createPlayer(inTournamentPlayers)
          .then(({ inTournamentPlayers, changeNumberPlayers }) => {
            const assignCompleted = BoxScoreAssignPlayerUniqid({ inTournamentPlayers, changeNumberPlayers });
            if (!assignCompleted) {
              setBatchGameTitle('新建球員時發生錯誤，請重新整理頁面');
              return;
            }

            createGames();
          })
          .catch((e) => {
            console.error(e);
            setBatchGameTitle('新建球員時發生錯誤，請重新整理頁面');
            return;
          });
      })
      .catch((e) => {
        console.error(e);
        setBatchGameTitle('獲取現有球員時發生錯誤，請重新整理頁面');
        return;
      });
  };

  const handleCancel = () => {
    setPendingGames([]);
    setTeamsInPending([]);
  };

  return (
    <StyledDiv>
      {pendingGames.length === 0 ? (
        <Fragment>
          <label>可一次丟入所有已整理過的 Excel 檔案，會自動新增球員跟比賽</label>
          <input
            type="file"
            accept=".xlsx,.xls"
            multiple={true}
            onChange={handleFileOnChange}
            key={inputRefresh}
            disabled={uploading}
          />
        </Fragment>
      ) : (
        <Fragment>
          <Button width="unset" color="red" onClick={handleSubmit} disabled={uploading}>
            送出
          </Button>
          <Button width="unset" color="transparentWithBorder" onClick={handleCancel} disabled={uploading}>
            取消
          </Button>
        </Fragment>
      )}
      <div className="notification">
        {!uploading &&
          pendingGames.map((pendingGame, i) => (
            <p key={i} className={pendingGame.error ? 'error' : 'success'}>
              {`${pendingGame.fileName} `}
              {pendingGame.error ? `解析失敗${pendingGame.error !== true ? `(${pendingGame.error})` : ''}` : `解析成功`}
            </p>
          ))}
        {uploadingGames.map((uploadingGame, i) => (
          <p key={i} className={uploadingGame.status}>
            {uploadingGame.message}
          </p>
        ))}
      </div>
      <div
        className="admin-only--content"
        style={{ background: 'linear-gradient(242deg, #094bcd, #28cfd7)', padding: '0.5rem' }}
      >
        <ProgressBar isOpen={true} title={batchGameTitle} completed={batchGameCompleted} />
      </div>
    </StyledDiv>
  );
};

export default BatchPbPUpload;
